import React, { useState, useRef } from "react";
import { useFormContext } from "../context/FormContext";
import { useToast } from "../context/ToastContext";
import {
  FormSection,
  FormRow,
  FormInput,
  FormTextarea,
  FormSelect,
  FormRadioGroup,
  SpecialtyTable,
} from "./shared/FormComponents";
import NABHDetails from "./conditional/NABHDetails";
import DownloadSection from "./DownloadSection";
import Captcha from "./shared/Captcha";
import { YES_NO_OPTIONS, AVAILABILITY_OPTIONS } from "../utils/constants";
import { validateFiles, createFileList } from "../utils/fileValidator";
import api from "../services/api";

const CombinedApplicationForm = () => {
  const [activeAccordion, setActiveAccordion] = useState("annexure");
  const { success, error } = useToast();
  const captchaRef = useRef(null);
  const {
    formData,
    handleInputChange,
    handleSpecialtyChange,
    addSpecialtyRow,
    removeSpecialtyRow,
    handleDocumentSubmissionChange,
    handleDocumentFileChange,
    removeDocumentFile,
    validateForm,
    isSubmitting,
    submitStatus,
    setIsSubmitting,
    setSubmitStatus,
    resetForm,
  } = useFormContext();

  const documentList = [
    {
      name: "Application Form & Profile of the Hospital",
      downloadable: true,
      filename: "ApplicationForm.pdf",
    },
    {
      name: "Annexure I – Details of Hospital (Own or Rented)",
      downloadable: true,
      filename: "ApplicationForm.pdf",
    },
    {
      name: "Annexure II – Certificate of Undertaking",
      downloadable: true,
      filename: "Annexure-II.pdf",
    },
    {
      name: "Annexure III – Terms & Conditions",
      downloadable: true,
      filename: "Annexure-III.pdf",
    },
    {
      name: "Certification",
      downloadable: true,
      filename: "Cetification(Annexure-IV).pdf",
    },
    { name: "Self-attested copy of NABH certificate", downloadable: false },
    { name: "Self-attested copy of NABL certificate", downloadable: false },
    { name: "Tariff List of the Hospital", downloadable: false },
    {
      name: "Self-attested copy of Registration certificate of the Hospital",
      downloadable: false,
    },
    {
      name: "Self-attested copies of Doctors' Registration Certificates",
      downloadable: false,
    },
    {
      name: "Self-attested copies of Registration & Qualifications of Technicians'",
      downloadable: false,
    },
    {
      name: "Self-attested copies of Registration & Qualifications of Nurses",
      downloadable: false,
    },
    { name: "Medical Coordinator Appointment Letter", downloadable: false },
    {
      name: "Self-attested copy of PCB License for Biomedical waste management",
      downloadable: false,
    },
    {
      name: "Self-attested copy of NOC issued by Fire Department",
      downloadable: false,
    },
    {
      name: "AERB Approval Letter for establishment of Radiology Dept.",
      downloadable: false,
    },
    { name: "Self-attested copy of PAN Card", downloadable: false },
    {
      name: "Bank Details (Attested Copy of Bank Passbook / Cancelled Cheque)",
      downloadable: false,
    },
    {
      name: "Audited balance sheet for last 3 financial years",
      downloadable: false,
    },
    { name: "Self-attested copy of Legal status", downloadable: false },
    {
      name: "Self-attested copy of Place of Registration",
      downloadable: false,
    },
    { name: "Place of Business", downloadable: false },
    {
      name: "Self-attested copy of Partnership Deed / AoA/ MoA",
      downloadable: false,
    },
    {
      name: "Whether empaneled by CGHS (Enclose self-attested copy)",
      downloadable: false,
    },
    {
      name: "Whether empaneled by State Govt. (Enclose self- attested copy)",
      downloadable: false,
    },
    { name: "Self-attested copy of ESI coverage Letter", downloadable: false },
    { name: "Self-attested copy of EPF coverage letter", downloadable: false },
    { name: "List of major equipment", downloadable: false },
    {
      name: "Pharmacy Drug License Certificate from 20 & 21",
      downloadable: false,
    },
  ];

  const getTotalFiles = () => {
    return Object.values(formData.documents).reduce((total, doc) => {
      if (doc.submitted === "Yes" && doc.files && Array.isArray(doc.files)) {
        return (
          total +
          doc.files.filter((fileObj) => {
            // Handle both old format (File) and new format ({id, file})
            return (
              fileObj instanceof File ||
              (fileObj && fileObj.file instanceof File)
            );
          }).length
        );
      }
      return total;
    }, 0);
  };

  /**
   * Handles file upload with validation
   * @param {FileList} files - Files selected by the user
   * @param {number} documentIndex - Index of the document in the form
   */
  const handleFileUpload = (files, documentIndex) => {
    if (!files || files.length === 0) {
      return;
    }

    const fileArray = Array.from(files);
    const { validFiles, invalidFiles } = validateFiles(fileArray);

    // Show error messages for invalid files
    if (invalidFiles.length > 0) {
      invalidFiles.forEach(({ error: errorMessage }) => {
        error(errorMessage);
      });
    }

    // Only process valid files
    if (validFiles.length > 0) {
      const fileList = createFileList(validFiles);
      handleDocumentFileChange(documentIndex, fileList);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      error("Please fix the errors before submitting");
      return;
    }

    // Validate CAPTCHA
    if (!captchaRef.current || !captchaRef.current.validate()) {
      error(
        "CAPTCHA verification failed. Please complete the reCAPTCHA verification."
      );
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("");

    try {
      // Prepare form data for API submission
      const formDataToSubmit = new FormData();

      // Add all form fields
      Object.keys(formData).forEach((key) => {
        if (key !== "documents" && key !== "specialties") {
          if (formData[key] !== "") {
            formDataToSubmit.append(key, formData[key]);
          }
        }
      });

      // Add specialties as JSON string
      if (formData.specialties && formData.specialties.length > 0) {
        formDataToSubmit.append(
          "specialties",
          JSON.stringify(formData.specialties)
        );
      }

      // Add document files (multiple files per document)
      // Only use files from current state, filter out any null/undefined
      Object.keys(formData.documents).forEach((key) => {
        const doc = formData.documents[key];
        if (doc.submitted === "Yes" && doc.files && Array.isArray(doc.files)) {
          doc.files
            .filter((fileObj) => {
              if (!fileObj) return false;
              // Handle both old format (File) and new format ({id, file})
              return fileObj instanceof File || fileObj.file instanceof File;
            })
            .forEach((fileObj) => {
              const file = fileObj instanceof File ? fileObj : fileObj.file;
              formDataToSubmit.append(`CheckList ${key}`, file);
            });
        }
      });

      // Add reCAPTCHA token
      const recaptchaToken = captchaRef.current?.getValue();
      if (recaptchaToken) {
        formDataToSubmit.append("recaptchaToken", recaptchaToken);
      }

      // Submit to API
      const response = await api.submitHospitalApplication(formDataToSubmit);

      success("Application submitted successfully!");
      setSubmitStatus("Application submitted successfully!");
      resetForm();

      // Reset CAPTCHA after successful submission
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Error submitting application. Please try again.";
      error(errorMessage);
      setSubmitStatus("Error submitting application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="combined-form-container">
      {/* Download Section */}
      <DownloadSection />

      <div className="accordion-container">
        {/* Accordion 1: Annexure I */}
        <div
          className={`accordion ${
            activeAccordion === "annexure" ? "active" : ""
          }`}
        >
          <div
            className={`accordion-header ${
              activeAccordion === "annexure" ? "active" : ""
            }`}
            onClick={() =>
              setActiveAccordion(
                activeAccordion === "annexure" ? null : "annexure"
              )
            }
          >
            <h2>Annexure I - Details of Hospital for Empanelment</h2>
            <span className="accordion-icon">
              {activeAccordion === "annexure" ? "−" : "+"}
            </span>
          </div>
          <div className="accordion-content">
            <form className="form-container">
              {/* Section A: Hospital Details */}
              <FormSection title="A. Details of the Hospital">
                <FormInput
                  id="name"
                  name="name"
                  label="1. Name of the Hospital"
                  required={true}
                />

                <FormRow>
                  <FormInput
                    id="city"
                    name="city"
                    label="2. Name of the City where Hospital is located"
                    required={true}
                  />
                </FormRow>

                <FormTextarea
                  id="address"
                  name="address"
                  label="3. Address of the Hospital"
                  required={true}
                  rows={3}
                />

                <FormRow>
                  <FormInput
                    id="telephone"
                    name="telephone"
                    label="4. Telephone No."
                    type="tel"
                  />
                  <FormInput
                    id="mobile"
                    name="mobile"
                    label="Mobile No."
                    type="tel"
                    required={true}
                  />
                </FormRow>

                <FormRow>
                  {/* <FormInput
                    id="fax"
                    name="fax"
                    label="5. Fax Number"
                    type="tel"
                  /> */}
                  <FormInput
                    id="email"
                    name="email"
                    label="5. Email Address"
                    type="email"
                    required={true}
                  />
                </FormRow>

                <FormInput
                  id="superintendent_name"
                  name="superintendent_name"
                  label="6. Name and Contact Details of Medical Superintendent/RMO"
                  required={true}
                />

                <FormRow>
                  <FormInput
                    id="superintendent_email"
                    name="superintendent_email"
                    label="7. Email ID"
                    type="email"
                  />
                  <FormInput
                    id="superintendent_contact"
                    name="superintendent_contact"
                    label="8. Telephone No./ Mobile No."
                    type="tel"
                    required={true}
                  />
                </FormRow>

                <FormInput
                  id="medical_coordinator"
                  name="medical_coordinator"
                  label="9. Name and Contact Details of Medical Coordinator"
                  required={true}
                />

                <FormRow>
                  <FormInput
                    id="coordinator_email"
                    name="coordinator_email"
                    label="10. Email ID"
                    type="email"
                  />
                  <FormInput
                    id="coordinator_contact"
                    name="coordinator_contact"
                    label="11. Telephone No./ Mobile No."
                    type="tel"
                    required={true}
                  />
                </FormRow>
              </FormSection>

              {/* Section B: NABH Accreditation */}
              <FormSection title="B. Details of NABH Accreditation">
                <FormRadioGroup
                  name="nabh_accredited"
                  label="1. Whether NABH Accredited"
                  options={YES_NO_OPTIONS}
                  required={true}
                />

                <NABHDetails />
              </FormSection>

              {/* Section C: Services */}
              <FormSection title="C. Details of Services Applied for">
                <SpecialtyTable />
              </FormSection>

              {/* Section D: Doctors */}
              <FormSection title="D. Availability of Doctors">
                <FormRow>
                  <FormInput
                    id="full_time_specialists"
                    name="full_time_specialists"
                    label="1. Number of Full time Specialists"
                    type="number"
                    min="0"
                  />
                  <FormInput
                    id="duty_doctors"
                    name="duty_doctors"
                    label="2. Number of Duty Doctors"
                    type="number"
                    min="0"
                  />
                </FormRow>

                <FormRow>
                  <FormInput
                    id="resident_medical_officers"
                    name="resident_medical_officers"
                    label="3. Number of Resident Medical Officers"
                    type="number"
                    min="0"
                  />
                  <FormInput
                    id="super_specialists"
                    name="super_specialists"
                    label="4. Number of Super specialists (if any)"
                    type="number"
                    min="0"
                  />
                </FormRow>

                <FormInput
                  id="doctors_on_call"
                  name="doctors_on_call"
                  label="5. Number of Doctors on Call"
                  type="number"
                  min="0"
                />
              </FormSection>

              {/* Section E: Nursing Care */}
              <FormSection title="E. Details of Nursing Care">
                <FormRow>
                  <FormInput
                    id="total_nurses"
                    name="total_nurses"
                    label="1. Total No of Nurses"
                    type="number"
                    min="0"
                  />
                  <FormInput
                    id="nursing_superintendent"
                    name="nursing_superintendent"
                    label="2. Name of the Nursing Superintendent"
                  />
                </FormRow>

                <FormRow>
                  <FormInput
                    id="patient_nurse_ratio_general"
                    name="patient_nurse_ratio_general"
                    label="3a. Patient: Nurse Ratio - General Ward (Norm 6:1)"
                    placeholder="e.g., 6:1"
                  />
                  <FormInput
                    id="patient_nurse_ratio_icu"
                    name="patient_nurse_ratio_icu"
                    label="3b. Patient: Nurse Ratio - ICCU/ICU (Norm 1:1)"
                    placeholder="e.g., 1:1"
                  />
                </FormRow>
              </FormSection>

              {/* Section F: Other Staff */}
              <FormSection title="F. Details of Other Staff">
                <FormRow>
                  <FormInput
                    id="lab_technicians"
                    name="lab_technicians"
                    label="1. No of Lab Technicians"
                    type="number"
                    min="0"
                  />
                  <FormInput
                    id="radiographers"
                    name="radiographers"
                    label="2. No of Radiographers"
                    type="number"
                    min="0"
                  />
                </FormRow>

                <FormRow>
                  <FormInput
                    id="physiotherapists"
                    name="physiotherapists"
                    label="3. No of Physiotherapists"
                    type="number"
                    min="0"
                  />
                  <FormInput
                    id="dieticians"
                    name="dieticians"
                    label="4. No of Dieticians"
                    type="number"
                    min="0"
                  />
                </FormRow>

                <FormRow>
                  <FormInput
                    id="admin_staff"
                    name="admin_staff"
                    label="5. No of Administrative Staff"
                    type="number"
                    min="0"
                  />
                  <FormInput
                    id="house_keeping"
                    name="house_keeping"
                    label="6. No of House Keeping Staff"
                    type="number"
                    min="0"
                  />
                </FormRow>

                <FormInput
                  id="security"
                  name="security"
                  label="7. No of Security Personnel"
                  type="number"
                  min="0"
                />
              </FormSection>

              {/* Section G: Infrastructure */}
              <FormSection title="G. Infrastructure Details of the Hospital">
                <FormRow>
                  <FormInput
                    id="total_beds"
                    name="total_beds"
                    label="1. Total No. of Beds"
                    type="number"
                    min="1"
                    required={true}
                  />
                  <FormInput
                    id="casualty_beds"
                    name="casualty_beds"
                    label="2. No of Beds in the Casualty/Emergency"
                    type="number"
                    min="0"
                  />
                </FormRow>

                <FormRow>
                  <FormInput
                    id="icu_beds"
                    name="icu_beds"
                    label="3. No of Beds in ICCU/ICU/HDU"
                    type="number"
                    min="0"
                  />
                  <FormInput
                    id="ventilators"
                    name="ventilators"
                    label="4. No of Ventilators"
                    type="number"
                    min="0"
                  />
                </FormRow>

                <FormRow>
                  <FormInput
                    id="general_ward_beds"
                    name="general_ward_beds"
                    label="5. No of General Ward Beds"
                    type="number"
                    min="0"
                  />
                  <FormInput
                    id="avg_opd_attendance"
                    name="avg_opd_attendance"
                    label="6. Average Daily OPD Attendance"
                    type="number"
                    min="0"
                  />
                </FormRow>

                <FormRow>
                  <FormInput
                    id="avg_bed_occupancy"
                    name="avg_bed_occupancy"
                    label="7. Average Bed Occupancy for the last one year (%)"
                    type="number"
                    min="0"
                    max="100"
                  />
                  <FormInput
                    id="total_area"
                    name="total_area"
                    label="8. Total Area of the Hospital (sq ft)"
                    type="number"
                    min="0"
                  />
                </FormRow>

                <FormRow>
                  <FormInput
                    id="opd_area"
                    name="opd_area"
                    label="9. Area allotted to the OPD (sq ft)"
                    type="number"
                    min="0"
                  />
                  <FormInput
                    id="ipd_area"
                    name="ipd_area"
                    label="10. Area allotted to the IPD (sq ft)"
                    type="number"
                    min="0"
                  />
                </FormRow>

                <FormRow>
                  <FormInput
                    id="wards"
                    name="wards"
                    label="11. No of Wards"
                    type="number"
                    min="0"
                  />
                  <FormInput
                    id="ward_dimensions"
                    name="ward_dimensions"
                    label="12. Dimensions of the Wards"
                    placeholder="e.g., 20x15 ft"
                  />
                </FormRow>

                <FormInput
                  id="alt_power"
                  name="alt_power"
                  label="13. Alternate Power Source"
                  placeholder="e.g., Generator, UPS, etc."
                />
              </FormSection>

              {/* Section H: Laboratory Services */}
              <FormSection title="H. Details of Laboratory Services">
                <FormRow>
                  <FormInput
                    id="biochemistry_head"
                    name="biochemistry_head"
                    label="1. Biochemistry - Head of the Department"
                  />
                  <FormInput
                    id="pathology_head"
                    name="pathology_head"
                    label="2. Pathology - Head of the Department"
                  />
                </FormRow>

                <FormInput
                  id="microbiology_head"
                  name="microbiology_head"
                  label="3. Microbiology - Head of the Department"
                />
              </FormSection>

              {/* Section I: Imaging Facilities */}
              <FormSection title="I. Details of Imaging Facilities">
                <FormRow>
                  <FormSelect
                    id="xray"
                    name="xray"
                    label="1. X Ray"
                    options={AVAILABILITY_OPTIONS}
                  />
                  <FormSelect
                    id="ultrasonography"
                    name="ultrasonography"
                    label="2. Ultrasonography"
                    options={AVAILABILITY_OPTIONS}
                  />
                </FormRow>

                <FormRow>
                  <FormSelect
                    id="mammography"
                    name="mammography"
                    label="3. Mammography"
                    options={AVAILABILITY_OPTIONS}
                  />
                  <FormSelect
                    id="ct_scan"
                    name="ct_scan"
                    label="4. CT Scan"
                    options={AVAILABILITY_OPTIONS}
                  />
                </FormRow>

                <FormSelect
                  id="mri_scan"
                  name="mri_scan"
                  label="5. MRI Scan"
                  options={AVAILABILITY_OPTIONS}
                />
              </FormSection>

              {/* Section J: Operation Theatres */}
              <FormSection title="J. Details of Operation Theatres">
                <FormRow>
                  <FormInput
                    id="operation_theatres"
                    name="operation_theatres"
                    label="1. Number of Operation Theatres"
                    type="number"
                    min="0"
                  />
                  <FormRadioGroup
                    name="septic_ot"
                    label="2. Whether there is separate OT for Septic Cases"
                    options={YES_NO_OPTIONS}
                  />
                </FormRow>

                <FormRadioGroup
                  name="ot_24x7"
                  label="3. Whether OT facility available around the Clock"
                  options={YES_NO_OPTIONS}
                />
              </FormSection>

              {/* Section K: Supportive Services */}
              <FormSection title="K. Details of Supportive Services">
                <FormRow>
                  <FormSelect
                    id="blood_bank"
                    name="blood_bank"
                    label="1. Blood Bank"
                    options={AVAILABILITY_OPTIONS}
                  />
                  <FormSelect
                    id="pharmacy_24hr"
                    name="pharmacy_24hr"
                    label="2. 24 Hour Pharmacy"
                    options={AVAILABILITY_OPTIONS}
                  />
                </FormRow>

                <FormRow>
                  <FormSelect
                    id="physiotherapy"
                    name="physiotherapy"
                    label="3. Physiotherapy"
                    options={AVAILABILITY_OPTIONS}
                  />
                  <FormSelect
                    id="cssd"
                    name="cssd"
                    label="4. CSSD"
                    options={AVAILABILITY_OPTIONS}
                  />
                </FormRow>

                <FormRow>
                  <FormSelect
                    id="canteen"
                    name="canteen"
                    label="5. In-house Canteen"
                    options={AVAILABILITY_OPTIONS}
                  />
                  <FormSelect
                    id="gas_plant"
                    name="gas_plant"
                    label="6. Gas Plant"
                    options={AVAILABILITY_OPTIONS}
                  />
                </FormRow>

                <FormSelect
                  id="medical_records"
                  name="medical_records"
                  label="7. Medical Records Department"
                  options={AVAILABILITY_OPTIONS}
                />
              </FormSection>

              {/* Section L: Other Tie Ups */}
              <FormSection title="L. Details of other Tie Ups">
                <FormRadioGroup
                  name="empaneled_cghs"
                  label="1. Whether your hospital is empaneled with CGHS"
                  options={YES_NO_OPTIONS}
                />

                <FormRadioGroup
                  name="recognized_aarogyasri"
                  label="2. Whether your hospital is recognized by State Govt. for Aarogyasri/EHS"
                  options={YES_NO_OPTIONS}
                />

                <FormInput
                  id="tpi_tieup"
                  name="tpi_tieup"
                  label="3. Any Tie Up with TPIs"
                  placeholder="Please specify if any"
                />
              </FormSection>

              {/* Section M: Biomedical Waste Management */}
              <FormSection title="M. Details of Biomedical Waste Management">
                <FormRadioGroup
                  name="biomedical_waste"
                  label="1. Whether your hospital is following the Biomedical waste management as per statutory requirements"
                  options={YES_NO_OPTIONS}
                />

                <FormInput
                  id="pcb_license"
                  name="pcb_license"
                  label="2. Please Provide PCB License No"
                />
              </FormSection>

              {/* Section N: Bank Account */}
              <FormSection title="N. Details of Hospital Bank Account">
                <FormRow>
                  <FormInput
                    id="bank_name"
                    name="bank_name"
                    label="1. Name of the Bank"
                    required={true}
                  />
                  <FormInput
                    id="bank_branch"
                    name="bank_branch"
                    label="2. Branch of the Bank"
                  />
                </FormRow>

                <FormRow>
                  <FormInput
                    id="account_number"
                    name="account_number"
                    label="3. Account Number"
                    type="number"
                    required={true}
                  />
                  <FormInput
                    id="ifsc_code"
                    name="ifsc_code"
                    label="4. IFSC Code"
                    required={true}
                    style={{ textTransform: "uppercase" }}
                  />
                </FormRow>

                <FormInput id="micr_no" name="micr_no" label="5. MICR No." />
              </FormSection>
            </form>
          </div>
        </div>

        {/* Accordion 2: Document Checklist */}
        <div
          className={`accordion ${
            activeAccordion === "checklist" ? "active" : ""
          }`}
        >
          <div
            className={`accordion-header ${
              activeAccordion === "checklist" ? "active" : ""
            }`}
            onClick={() =>
              setActiveAccordion(
                activeAccordion === "checklist" ? null : "checklist"
              )
            }
          >
            <h2>Document Checklist</h2>
            <span className="accordion-icon">
              {activeAccordion === "checklist" ? "−" : "+"}
            </span>
          </div>
          <div className="accordion-content">
            <div className="form-container">
              <div className="form-section">
                <div className="checklist-table-container">
                  <table className="checklist-table">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Name of the Document</th>
                        <th>Submitted Yes/No/NA</th>
                        <th>Upload File</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentList.map((doc, index) => (
                        <tr key={index + 1}>
                          <td>{index + 1}</td>
                          <td className="document-name">
                            <div className="document-name-container">
                              <span>{doc.name}</span>
                              {doc.downloadable && (
                                <button
                                  className="download-link-btn"
                                  onClick={() => {
                                    const link = document.createElement("a");
                                    link.href = `/assets/${doc.filename}`;
                                    link.download = doc.filename;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }}
                                  title={`Download ${doc.filename}`}
                                >
                                  📥 Download
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <select
                              value={formData.documents[index + 1].submitted}
                              onChange={(e) =>
                                handleDocumentSubmissionChange(
                                  index + 1,
                                  e.target.value
                                )
                              }
                              className="submission-select"
                            >
                              <option value="">Select</option>
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                              <option value="NA">NA</option>
                            </select>
                          </td>
                          <td>
                            {formData.documents[index + 1].submitted ===
                            "Yes" ? (
                              <div className="file-upload-container">
                                <input
                                  type="file"
                                  id={`file-${index + 1}`}
                                  key={`file-input-${index + 1}-${
                                    formData.documents[index + 1].files
                                      ?.length || 0
                                  }`}
                                  onChange={(e) => {
                                    if (
                                      e.target.files &&
                                      e.target.files.length > 0
                                    ) {
                                      handleFileUpload(
                                        e.target.files,
                                        index + 1
                                      );
                                      // Clear the input value so selecting same files again triggers onChange
                                      e.target.value = "";
                                    }
                                  }}
                                  className="file-input"
                                  accept=".pdf,application/pdf"
                                  multiple
                                />
                                <label
                                  htmlFor={`file-${index + 1}`}
                                  className="file-upload-btn"
                                >
                                  Choose Files
                                </label>
                                {formData.documents[index + 1].files &&
                                  formData.documents[index + 1].files.length >
                                    0 && (
                                    <div className="uploaded-files-list">
                                      {formData.documents[index + 1].files.map(
                                        (fileObj, idx) => {
                                          // Handle both old format (File) and new format ({id, file})
                                          const file =
                                            fileObj instanceof File
                                              ? fileObj
                                              : fileObj.file;
                                          const fileId =
                                            fileObj instanceof File
                                              ? `fallback-${idx}`
                                              : fileObj.id;

                                          return (
                                            <div
                                              key={fileId}
                                              className="uploaded-file-item"
                                            >
                                              <span className="file-name">
                                                {file.name}
                                              </span>
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  const docIndex = index + 1;
                                                  removeDocumentFile(
                                                    docIndex,
                                                    fileId
                                                  );
                                                  // Clear the file input for this document
                                                  const fileInput =
                                                    document.getElementById(
                                                      `file-${docIndex}`
                                                    );
                                                  if (fileInput) {
                                                    fileInput.value = "";
                                                  }
                                                }}
                                                className="remove-file-btn"
                                                title="Remove file"
                                              >
                                                ✕
                                              </button>
                                            </div>
                                          );
                                        }
                                      )}
                                    </div>
                                  )}
                              </div>
                            ) : (
                              <span className="not-applicable">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="checklist-summary">
                  <div className="summary-item">
                    <strong>Total Documents Submitted: </strong>
                    <span className="total-count">{getTotalFiles()}</span>
                  </div>
                  <div className="summary-item">
                    <strong>Total Documents Required: </strong>
                    <span className="total-required">
                      {
                        Object.values(formData.documents).filter(
                          (doc) => doc.submitted === "Yes"
                        ).length
                      }
                    </span>
                  </div>
                </div>

                <div className="checklist-note">
                  <p>
                    <strong>Note:</strong> Please arrange the enclosures as per
                    checklist
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Section */}
      <div className="submit-section">
        {submitStatus && (
          <div
            className={
              submitStatus.includes("successfully")
                ? "success-message"
                : "error-message"
            }
          >
            {submitStatus}
          </div>
        )}

        {/* CAPTCHA Section */}
        <Captcha ref={captchaRef} />

        <button
          type="button"
          className="submit-btn"
          disabled={isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting ? "Submitting..." : "Submit Application"}
        </button>
        <p style={{ marginTop: "1rem", fontSize: "0.9rem", color: "#666" }}>
          Note: Please select NA for fields that are not applicable.
        </p>
      </div>
    </div>
  );
};

export default CombinedApplicationForm;
